import * as prod from 'react/jsx-runtime';
import rehypeKatex from 'rehype-katex';
import rehypeParse from 'rehype-parse';
import rehypeRaw from 'rehype-raw';
import rehypeReact from 'rehype-react';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import { type VFile } from 'vfile';

interface TableProps {
  headers: string[];
  rows: string[][];
}

export const markdownTable = ({ headers, rows }: TableProps) => `<div class='tableWrapper'>
<table>
<thead>
<tr>
${headers.map((h) => `<th>${h}</th>`).join('')}
</tr>
</thead>
<tbody>
${rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}
</tbody>
</table>
</div>
`;

export interface MdRenderOptions {
  imageUrl?: string;
  replaceRegex?: [string, string][];
}

export const convertMarkdownToHtml = (
  text: string,
  { imageUrl, replaceRegex }: MdRenderOptions = {},
): Promise<string | undefined> => {
  return (
    unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkMath, { singleDollarTextMath: false })
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw)
      // NOTE : https://github.com/rehypejs/rehype-sanitize#example-math
      .use(rehypeSanitize, {
        ...defaultSchema,
        attributes: {
          ...defaultSchema.attributes,
          div: [...(defaultSchema.attributes!.div || []), ['className', 'math', 'math-display']],
          span: [...(defaultSchema.attributes!.span || []), ['className', 'math', 'math-inline']],
        },
      })
      .use(rehypeKatex)
      .use(rehypeStringify)
      .process(text)
      .then((mdText: VFile) => {
        let result = String(mdText)
          .replace(/<table>/g, '<div class="tableWrapper"><table>')
          .replace(/<\/table>/g, '</table></div>');
        if (imageUrl) result = result.replace(/<img src="/g, `<img src="${imageUrl}`);
        if (replaceRegex) {
          replaceRegex.forEach(([regex, value]) => {
            result = result.replace(new RegExp(regex, 'g'), value);
          });
        }
        return result;
      })
  );
};

/* oxlint-disable @typescript-eslint/no-explicit-any */
interface Production {
  Fragment: any;
  jsx: any;
  jsxs: any;
}
const production: Production = { Fragment: prod.Fragment, jsx: prod.jsx, jsxs: prod.jsxs };

export const convertHtmlToReactElement = (HTMLString: string): Promise<VFile & { result: React.JSX.Element }> =>
  unified().use(rehypeParse, { fragment: true }).use(rehypeReact, production).process(HTMLString);
